import { Platform, StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import React from "react";
import { useTheme } from "@/hooks/useTheme";
import { Stepper } from "@/components/ui/Stepper";
import { Button } from "@/components/ui/Button";
import { useStorage } from "@/hooks/useStorage";
import { TableFooterProps } from "@/components/Table/props";
import Animated, { measure, useAnimatedRef } from "react-native-reanimated";
import useContextMenu from "@/hooks/useContextMenu";
import * as Device from "expo-device";

export const TableFooter = ({
  style,
  currentPage,
  resultsPerPage,
  setResultsPerPage,
  resultsChoices = [10, 20, 30],
  setPage,
  pageCount,
  selectedRows,
  loading,
  total = 0,
  filtered = 0,
  showing = 0,
  isSearching = false,
  isFiltering = false,
}: TableFooterProps) => {
  const { colors } = useTheme();
  const storage = useStorage();
  const ref = useAnimatedRef();
  const contextMenu = useContextMenu();
  const mobileLayout = Platform.OS !== "web" || Device.deviceType === Device.DeviceType.PHONE;
  // const [isOpen, setIsOpen] = useState<boolean>(false);

  const handlePress = (n: number) => {
    setResultsPerPage(n);
    setPage(0);
    storage.saveFast("resultsPerPage", n.toString());
    contextMenu.setVisible(false);
  };

  const stepperContent = (
    <View style={[styles.stepperContent, { flexDirection: "row" }]}>
      <ThemedText>
        {currentPage + 1} of {pageCount}
      </ThemedText>
    </View>
  );

  const contextMenuContent = (
    <View style={styles.menu}>
      {resultsChoices.map((c, i) => (
        <Button key={i} style={styles.menuButton} text={`${c}`} onPress={() => handlePress(c)} />
      ))}
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.card, borderColor: colors.border }, style]}>
      <Animated.View ref={ref} style={styles.results}>
        {!mobileLayout ? <ThemedText style={{ color: colors.placeholder }}>Results per page: </ThemedText> : null}
        {resultsChoices.length > 1 ? (
          <Button
            enabled={!loading}
            iconPosition="right"
            style={[styles.button]}
            text={` ${resultsPerPage} `}
            onPress={() => {
              const layout = measure(ref);
              const x = (layout?.pageX ?? 0) + (layout?.width ?? 0) - 100;
              const y = (layout?.pageY ?? 0) - (layout?.height ?? 0) - 28 * resultsChoices.length + 4;
              contextMenu.setLocation({ x, y });
              contextMenu.setMenuContent(contextMenuContent);
              contextMenu.setVisible(true);
            }}
            icon="chevron-up"
          />
        ) : (
          <ThemedText style={{ color: colors.placeholder }}>{resultsPerPage}</ThemedText>
        )}
      </Animated.View>
      <View style={styles.statsContainer}>
        {/* Total */}
        <View style={[styles.stat, { backgroundColor: colors.backgroundSecondary }]}>
          <ThemedText style={[styles.statCount, { color: colors.text }]}>{total.toLocaleString()}</ThemedText>
          <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>total</ThemedText>
        </View>

        {/* Filtered - only show when filters are active */}
        {isFiltering && filtered !== total && (
          <>
            <ThemedText style={[styles.separator, { color: colors.placeholder }]}>·</ThemedText>
            <View style={[styles.stat, { backgroundColor: colors.backgroundSecondary }]}>
              <ThemedText style={[styles.statCount, { color: colors.text }]}>{filtered.toLocaleString()}</ThemedText>
              <ThemedText style={[styles.statLabel, { color: colors.textSecondary }]}>filtered</ThemedText>
            </View>
          </>
        )}

        {/* Showing - only show when searching and different from filtered */}
        {isSearching && showing !== filtered && (
          <>
            <ThemedText style={[styles.separator, { color: colors.placeholder }]}>·</ThemedText>
            <View style={[styles.stat, { backgroundColor: colors.primary + "20" }]}>
              <ThemedText style={[styles.statCount, { color: colors.primary }]}>{showing.toLocaleString()}</ThemedText>
              <ThemedText style={[styles.statLabel, { color: colors.primary }]}>showing</ThemedText>
            </View>
          </>
        )}

        {/* Selected - styled to match stats pills */}
        {selectedRows > 0 && (
          <>
            <ThemedText style={[styles.separator, { color: colors.placeholder }]}>·</ThemedText>
            <View style={[styles.stat, { backgroundColor: colors.primary + "20" }]}>
              <ThemedText style={[styles.statCount, { color: colors.primary }]}>
                {selectedRows.toLocaleString()}
              </ThemedText>
              <ThemedText style={[styles.statLabel, { color: colors.primary }]}>selected</ThemedText>
            </View>
          </>
        )}
      </View>
      <Stepper
        canStepLeft={currentPage > 0 && !loading}
        onStepLeft={() => setPage(currentPage - 1)}
        onJumpLeft={() => setPage(0)}
        canStepRight={currentPage < pageCount - 1 && !loading}
        onStepRight={() => setPage(currentPage + 1)}
        onJumpRight={() => setPage(pageCount - 1)}
        center={stepperContent}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderWidth: StyleSheet.hairlineWidth,
    justifyContent: "space-between",
    alignItems: "center",
    padding: 4,
    borderRadius: 6,
  },
  results: {
    flexDirection: "row",
  },
  statsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  stat: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  statCount: {
    fontSize: 13,
    fontWeight: "600",
  },
  statLabel: {
    fontSize: 12,
  },
  separator: {
    fontSize: 16,
    fontWeight: "300",
  },
  menu: {
    // bottom: 38,
    // right: 0,
  },
  button: {
    backgroundColor: "transparent",
    paddingHorizontal: 4,
  },
  menuButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
  },
  stepperContent: {
    justifyContent: "center",
    minWidth: 60,
  },
});
